import React, {Component, ReactElement, ReactNode} from 'react'

import {SessionStorageHelper} from '../../../SessionStorageHelper'
import {Nullable} from '../../../app/TypeHelpers'
import NatDisasterRepo from './repository/NatDisasterRepo'
import {DisasterPreventionsInfoDetail} from './repository/NatGetDisasterPreventionsInfo'
import NatCondominiumRepo from '../condominium/repository/NatCondominiumRepo'
import EventEmitterExAdmin from '../../../components/shared/pageBaseComponents/EventEmitterExAdmin'
import {AdminPagePropsSet, createAdminPage} from '../../../components/shared/hoc/createAdminPage'
import {NatGetCondominium} from '../condominium/repository/NatGetCondominium'
import {AppAdminContext} from '../../../app/AppAdminContext'
import {
    PageFooterArea,
    PageFotterAreaLeft,
    PageFotterAreaRight,
    PageMainArea
} from '../../../components/shared/hoc/withAdminPageTemplate'
import {BackLink, DeleteLink} from '../../../styledComponents/adminPage/Links'
import AuthorityChecker from '../../../components/AuthorityChecker'
import {HKConstants} from '../../../HKConstants'
import {Box, Link, Table, TableBody, TableRow, TableCell} from '@mui/material'
import {StyledTableCaption, TableRowName} from '../../../styledComponents/adminPage/Table'
import {ADMINISTORATOR_MODE, DISASTER_RELEASE_SETTING, MESSAGE_TYPE} from '../../../CodeMaster'
import styled from 'styled-components'
import {AdminPrimaryButton} from '../../../styledComponents/adminPage/Buttons'
import {Messages} from '../../../Messages'
import {DeleteAlertIcon} from '../../../styledComponents/adminPage/IconCreator'
import {Colors} from '../../../styledComponents/Constants'
import {HeadingAreaMessageParams} from '../../../components/shared/pageBaseComponents/HeadingAreaMessageComponent'
import {faFilePdf} from '@fortawesome/free-solid-svg-icons'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {Location} from 'react-router-dom'

const SELECTED_ADMINISTORATOR_MODE: string = 'selectedMode'

interface Props {
    condominiumCd: string
    disasterPreventionId: number
    condominiumRepo: NatCondominiumRepo
    disasterRepo: NatDisasterRepo
    eventEmitter: EventEmitterExAdmin
    location: Location
}

interface State {
    condominiumName: string
    disasterInfo: DisasterPreventionsInfoDetail
    isLoadCompleted: boolean
}

class NatAdminDisasterPreventionsDetailsPage extends Component<Props & AdminPagePropsSet, State> {
    mode: Nullable<string>;

    constructor(props: Props & AdminPagePropsSet) {
        super(props)
        this.mode = SessionStorageHelper.getStringItemOrNull(SELECTED_ADMINISTORATOR_MODE);
        this.state = {
            condominiumName: '',
            disasterInfo: {
                disasterPreventionId: 0,
                title: '',
                description: '',
                releaseSetting: '',
                modifierUserFamilyName: '',
                modifierUserFirstName: '',
                version: 0,
                disasterPreventionFile: null,
            },
            isLoadCompleted: false
        }

        this.onClickEditButton = this.onClickEditButton.bind(this);
        this.onClickDeleteLink = this.onClickDeleteLink.bind(this);
        this.handleRegisterCancelClick = this.handleRegisterCancelClick.bind(this);
        this.handleDeleteOkClick = this.handleDeleteOkClick.bind(this);
    }

    async componentDidMount(): Promise<void> {
        await this.props.eventEmitter.showLoading()
        try {
            const {condominiumCd, disasterPreventionId, condominiumRepo, disasterRepo, eventEmitter} = this.props

            // 全体モードの場合は物件名を表示するために物件情報を取得する
            if (this.mode === ADMINISTORATOR_MODE.ALL.value) {
                const condominiumResponse: Nullable<NatGetCondominium> = await this.props.asyncCallFunction(
                    condominiumRepo.getCondominium.bind(condominiumRepo, condominiumCd),
                    (httpErrorOrNetworkError) => {
                        eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                    },
                )
                if (condominiumResponse?.details) {
                    this.setState({
                        condominiumName: condominiumResponse.details.condominiumName
                    })
                }
            }

            // 防災情報取得
            const response = await this.props.asyncCallFunction(
                disasterRepo.getDisasterInfo.bind(disasterRepo, condominiumCd, disasterPreventionId),
                (httpErrorOrNetworkError) => {
                    eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                },
            )
            if (response?.details) {
                this.setState({
                    disasterInfo: response.details
                })
            }
            this.showInitialMessage();
        } finally {
            this.props.eventEmitter.closeLoading()
            this.setState({isLoadCompleted: true})
        }
    }

    showInitialMessage(): void {
        if (!this.props.location || !this.props.location.state) {
            return
        }
        const initialMessage: HeadingAreaMessageParams = this.props.location.state.initialMessage;
        this.props.navigate(this.props.location.pathname, {replace: true, state: {}});
        this.props.eventEmitter.showHeadingAreaMessage(initialMessage);
    }

    onClickEditButton(): void {
        this.props.navigate(
            `/admin/condominiums/${this.props.condominiumCd}/disasters/preventionEdit/${this.props.disasterPreventionId}`
        )
    }

    async onClickDeleteLink(): Promise<void> {
        await this.props.eventEmitter.showDialog({
                title: '削除確認',
                titleIcon: <DeleteAlertIcon/>,
                errorLevel: Messages.IOPCC0027.errorLevel,
                message: Messages.IOPCC0027.toString(this.state.disasterInfo.title),
                buttonProps: [
                    {
                        isDefault: false,
                        buttonLabel: '削除する',
                        buttonHandler: this.handleDeleteOkClick,
                        color: Colors.Alert
                    },
                    {
                        isDefault: false,
                        buttonLabel: 'キャンセル',
                        buttonHandler: this.handleRegisterCancelClick,
                    },
                ],
            },
        );
    }

    async handleDeleteOkClick(): Promise<void> {
        const {condominiumCd, disasterPreventionId, disasterRepo, eventEmitter} = this.props
        const {disasterInfo} = this.state

        this.props.eventEmitter.closeDialog();
        await this.props.eventEmitter.showLoading();
        try {
            // 防災情報削除
            const response = await this.props.asyncCallFunction(
                disasterRepo.deleteDisasterInfo.bind(disasterRepo, condominiumCd, disasterPreventionId),
                (httpErrorOrNetworkError) => {
                    eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                },
            )
            if (response?.requestError === null) {
                this.props.navigate(`/admin/condominiums/${condominiumCd}/disasters/preventions`,
                    {
                        state: {
                            initialMessage: {
                                title: null,
                                messageLevel: MESSAGE_TYPE.INFORMATION,
                                detail: Messages.IOPCC0006.toString('防災情報', disasterInfo.title)
                            },
                        },
                    },
                )
            }
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    handleRegisterCancelClick(): void {
        this.props.eventEmitter.closeDialog();
    }


    renderEditArea(): ReactElement {
        return (
            <Box mb={'15px'}>
                <PageFooterArea>
                    <PageFotterAreaLeft>
                        <AdminPrimaryButton onClick={this.onClickEditButton}>
                            編集する
                        </AdminPrimaryButton>
                    </PageFotterAreaLeft>
                    <PageFotterAreaRight>
                        <DeleteLink linkColor={Colors.Alert} onClick={this.onClickDeleteLink}>
                            防災情報を削除する
                        </DeleteLink>
                    </PageFotterAreaRight>
                </PageFooterArea>
            </Box>
        )
    }

    renderDisasterPreventionsInfo(): ReactNode {
        const {condominiumName, disasterInfo} = this.state

        return (
            <>
                <Table>
                    <StyledTableCaption>防災情報</StyledTableCaption>
                    <TableBody>
                        {this.mode === ADMINISTORATOR_MODE.ALL.value &&
                            <TableRow>
                                <TableRowName>物件名</TableRowName>
                                <TableCell colSpan={3}>
                                    {condominiumName}
                                </TableCell>
                            </TableRow>
                        }
                        <TableRow>
                            <TableRowName>タイトル</TableRowName>
                            <TableCell colSpan={3}>
                                {disasterInfo.title}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableRowName>本文</TableRowName>
                            <TableCellWordBreakAll colSpan={3}>
                                <div dangerouslySetInnerHTML={{__html: disasterInfo.description}}/>
                            </TableCellWordBreakAll>
                        </TableRow>
                        {disasterInfo.disasterPreventionFile?.map((disasterPreventionFile, index) => (
                            <TableRow key={'disasterPreventionFile_' + disasterPreventionFile.fileNumber}>
                                {index === 0 &&
                                    <TableRowName rowSpan={disasterInfo.disasterPreventionFile?.length}>
                                        掲載資料
                                    </TableRowName>
                                }
                                <TableCell>
                                    <FileCellWrapper>
                                        <AttachmentFileStyle>
                                            <FontAwesomeIcon icon={faFilePdf}/>
                                        </AttachmentFileStyle>
                                        <Link href={disasterPreventionFile.path} target={'_blank'}>
                                            {disasterPreventionFile.publishMaterialName}
                                        </Link>
                                    </FileCellWrapper>

                                </TableCell>
                                <TableCell>
                                    <OrderCellWrapper>
                                        並び順 {disasterPreventionFile.sortOrder}
                                    </OrderCellWrapper>
                                </TableCell>
                            </TableRow>
                        ))
                        }
                        <TableRow>
                            <TableRowName>公開区分</TableRowName>
                            <TableCell colSpan={3}>
                                {DISASTER_RELEASE_SETTING.codeToEnumConstants(disasterInfo.releaseSetting).label}
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </>
        )
    }

    renderPageFooterArea(): ReactElement {
        const {condominiumCd} = this.props

        return (
            <PageFotterAreaLeft>
                <BackLink to={`/admin/condominiums/${condominiumCd}/disasters/preventions`}>
                    防災情報一覧へ戻る
                </BackLink>
            </PageFotterAreaLeft>
        )
    }


    render(): ReactElement {
        if (!this.state.isLoadCompleted) {
            return (<></>)
        }

        return (
            <>
                <AuthorityChecker permittedAuthorities={HKConstants.CMN.AUTH.EDIT_DISASTER_PREVENTION}>
                    {this.renderEditArea()}
                </AuthorityChecker>
                <PageMainArea>
                    {this.renderDisasterPreventionsInfo()}
                </PageMainArea>
                <PageFooterArea>
                    {this.renderPageFooterArea()}
                </PageFooterArea>
            </>
        )
    }
}

NatAdminDisasterPreventionsDetailsPage.contextType = AppAdminContext
export default createAdminPage(NatAdminDisasterPreventionsDetailsPage)

const TableCellWordBreakAll = styled(TableCell)`
    &.MuiTableCell-root {
        word-break: break-all;
    }
`

const FileCellWrapper = styled.div`
    display: flex;
    flex: 1 1 auto;
    align-items: flex-end;
    justify-content: flex-start;
`

const OrderCellWrapper = styled.div`
    display: flex;
    align-items: flex-end;
    justify-content: flex-end;
    padding-right: 120px;
`
const AttachmentFileStyle = styled.div`
  font-size: 21px;
  line-height: 1;
  min-width: 21px;
  text-align: center;
  margin-right: 8px;
  color: rgba(0,0,0,0.54);
`